<template>
  <div class="py-2 w-full flex flex-col gap-y-2 border-t border-brand-gray-300">
    <CollapsibleSection body-class="flex flex-col md:flex-row flex-wrap text-brand-black-secondary">
      <div class="pb-2 w-full md:w-1/2 flex flex-col md:flex-row font-sans text-xs">
        <h3 class="w-1/3">{{ $t('administration') }}:</h3>
        <span class="flex-1 text-start text-brand-black-body max-w-prose">
          {{ request.entity.admninstrativeRegions.text }}
        </span>
      </div>
      <div class="pb-2 w-full md:w-1/2 flex flex-col md:flex-row font-sans text-xs">
        <h3 class="w-1/3">{{ $t('RequestNumber') }}:</h3>
        <span class="flex-1 text-start text-brand-black-body max-w-prose">
          {{ request.entity.requestNumber }}
        </span>
      </div>
      <div class="pb-2 w-full md:w-1/2 flex flex-col md:flex-row font-sans text-xs">
        <h3 class="w-1/3">{{ $t('ServiceTransactions') }}:</h3>
        <span class="flex-1 text-start text-brand-black-body max-w-prose">
          {{ request.entity.serviceTransactionName }}
        </span>
      </div>
      <div class="pb-2 w-full md:w-1/2 flex flex-col md:flex-row font-sans text-xs">
        <h3 class="w-1/3">{{ $t('applicationFees') }}:</h3>
        <span class="flex-1 text-start text-brand-black-body max-w-prose">
          {{ intlNumberFormat.format(request.entity.paidAmount) }}
        </span>
      </div>
      <div class="pb-2 w-full md:w-1/2 flex flex-col md:flex-row font-sans text-xs">
        <h3 class="w-1/3">{{ $t('receiptNumber') }}:</h3>
        <span class="flex-1 text-start text-brand-black-body max-w-prose">
          {{ request.entity.receiptNumber }}
        </span>
      </div>
      <div class="pb-2 w-full md:w-1/2 flex flex-col md:flex-row font-sans text-xs">
        <h3 class="w-1/3">{{ $t('requestDate') }}:</h3>
        <span class="flex-1 text-start text-brand-black-body max-w-prose">
          {{ formatDate(request.entity.requestDate) }}
        </span>
      </div>
      <div
        v-if="request.entity.shipmentNumber"
        class="pb-2 w-full md:w-1/2 flex flex-col md:flex-row font-sans text-xs"
      >
        <h3 class="w-1/3">{{ $t('shipmentNumber') }}:</h3>
        <span class="flex-1 text-start text-brand-black-body max-w-prose">
          {{ request.entity.shipmentNumber }}
        </span>
      </div>
      <div
        v-if="request.entity.lookupDeliveryCompany && request.entity.shipmentNumber"
        class="pb-2 w-full md:w-1/2 flex flex-col md:flex-row font-sans text-xs"
      >
        <h3 class="w-1/3">{{ $t('deliveryCompany') }}:</h3>
        <span class="flex-1 text-start text-brand-black-body max-w-prose">
          {{ request.entity.lookupDeliveryCompany }}
        </span>
      </div>
      <div
        v-if="
          request.entity.refundedFeesAmount &&
          request.entity.ServiceTransId == icaRefund &&
          request.isApproved
        "
        class="pb-2 w-full md:w-1/2 flex flex-col md:flex-row font-sans text-xs"
      >
        <h3 class="w-1/3">{{ $t('refundedAmount') }}:</h3>
        <span class="flex-1 text-start text-brand-black-body max-w-prose">
          {{ intlNumberFormat.format(request.entity.refundedFeesAmount) }}
        </span>
      </div>
    </CollapsibleSection>

    <div v-if="popoverMenu.length > 0" class="w-full flex justify-end">
      <tippy
        :theme="useThemeStore().currentScheme == 'light' ? 'light-border' : 'dark-border'"
        :arrow="false"
        :offset="[0, 4]"
        interactive
        trigger="click"
        v-bind="{ ...(maxDate && { maxDate: new Date() }) }"
        placement="bottom-end"
      >
        <button class="rounded p-2 bg-gray-600" @click.prevent="">
          <faBars class="w-3 fill-white" />
        </button>
        <template #content="{ hide }">
          <div class="flex flex-col gap-y-2 max-h-[12rem] overflow-y-auto">
            <button
              :key="key"
              v-for="(menuItem, key) in popoverMenu"
              class="text-start px-3 py-1.5 text-sm text-brand-black-secondary hover:text-primary-700 hover:bg-gray-200"
              @click="
                () => {
                  menuItem.onClick()
                  hide()
                }
              "
            >
              {{ $t(menuItem.label) }}
            </button>
          </div>
        </template>
      </tippy>
    </div>

    <DataTable
      v-if="request.applicationHistory"
      :default-headers="['requestActions', 'transactionDate', 'actionComment']"
      :data-set="requestHistoryDataSet"
    >
      <template #cell="{ columnName, columnValue }">
        <template v-if="columnName == 'actionComment' && columnValue">
          {{ !!columnValue }}
          <p v-html="columnValue"></p>
        </template>
      </template>
    </DataTable>

    <div
      v-if="isRelatedRequestsFetched"
      class="pt-8 flex flex-col gap-y-1 border-t border-brand-gray-250"
    >
      <h3 class="text-primary text-lg">
        {{ $t('relatedRequests') }}
      </h3>
      <DataTable
        :default-headers="['requestNumber', 'service', 'lastActionId', 'lastActoinDate']"
        :data-set="relatedRequestsDataSet"
      >
        <template #emptyBody>
          <tr>
            <td colspan="4">
              <div class="w-full p-1.5">
                {{ $t('NoRecordFound') }}
              </div>
            </td>
          </tr>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import CollapsibleSection from '@/components/Ui/CollapsibleSection.vue'
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref } from 'vue'
import { DateTime } from 'luxon'
import DataTable from '@/components/DataTable/index.vue'
import client_lookups from '@/assets/json/clientLookups.json'
import faBars from '@/assets/icons/faBars.svg?component'
import { useThemeStore } from '@/stores/theme'
import axiosInstance from '@/services'
import client_config from '@/assets/json/client_config.json'

const props = defineProps({
  request: Object
})

const {
  ServiceTransactions: { icaRefund },
  requestActions: { approved, waitingForFingerprint },
  application: { guest },
  eidaServices: { replaceIDCardForCitizen },
  module: { ICAandResi, ICA }
} = client_lookups

const { showAppointmentsBooking, allowedAdministrativeRegionsForAppointment } = client_config

const relatedRequests = ref([])
const isRelatedRequestsFetched = ref(false)
const externalRequestActions = ref([])

const intlNumberFormat = Intl.NumberFormat('AE-en', { style: 'currency', currency: 'AED' })
const formatDate = (date) => DateTime.fromISO(date).toFormat('dd/MM/yyyy HH:mm:ss a')

const popoverMenu = computed(() => {
  if (!props.request) return []

  const {
    entity: {
      serviceTransId,
      lookupLastActionId,
      applicationId,
      module: { id: moduleId },
      administrativeRegionId
    },
    isAllowedToDownloadEid
  } = props.request

  const menu = []

  const servicesThatCanBePrinted = [376, 460, 461, 162]
  if (servicesThatCanBePrinted.includes(serviceTransId)) {
    menu.push({
      label: 'PrintFees',
      onClick: printReceipt
    })
  }

  if (lookupLastActionId == approved && serviceTransId == 162) {
    menu.push({
      label: 'PrintCardReport',
      onClick: printEstablishmentCard
    })
  }

  menu.push({
    label: 'relatedRequests',
    onClick: fetchRelatedRequests
  })

  if (isAllowedToDownloadEid) {
    menu.push({
      label: 'PrintApplication',
      onClick: showDownloadEidModal
    })
  }

  let canUpdateAppointment

  if (applicationId == guest && serviceTransId == replaceIDCardForCitizen) {
    canUpdateAppointment = false
  } else {
    canUpdateAppointment = [ICAandResi, ICA].includes(moduleId) && waitingForFingerprint
  }

  if (
    canUpdateAppointment &&
    showAppointmentsBooking &&
    allowedAdministrativeRegionsForAppointment.includes(administrativeRegionId)
  ) {
    menu.push({
      label: 'updateAppointment',
      onClick: redirectToAppointmentUpdate
    })
  }

  if (props.request.entity.applicationId == guest) {
    menu.push({
      label: 'mycomplaints',
      onClick: viewTicket
    })
  }

  return menu
})

const requestHistoryDataSet = computed(() => {
  if (!props.request) return []

  return props.request.applicationHistory.map((a) => {
    const actionComment =
      a.action.id == 64 ? 'showVirtualMeetingDetails' : `<p>${a.actionComment}</p>`

    return {
      requestActions: a.action.text,
      transactionDate: formatDate(a.date),
      actionComment: a.actionComment && actionComment
    }
  })
})

const relatedRequestsDataSet = computed(() => {
  if ((relatedRequests.value ?? []).length === 0) return []

  return relatedRequests.value.map((r) => {
    const requestActions = externalRequestActions.value?.find((e) => e.id == r.lastActionId)
    return {
      requestNumber: r.requestNumber,
      service: r.serviceTransactionName,
      lastActionId: requestActions?.text,
      lastActoinDate: formatDate(r.lastActionDate)
    }
  })
})

async function fetchRelatedRequests() {
  if (!props.request?.entity?.requestNumber) return
  const path = `/api/Landing/${props.request?.entity?.requestNumber}/relatedRequests`
  const response = await axiosInstance.get(path)
  isRelatedRequestsFetched.value = true

  relatedRequests.value = response
}

async function printReceipt() {
  console.info('TODO: printReceipt')
}

async function printEstablishmentCard() {
  console.info('TODO: printEstablishmentCard')
}

async function showDownloadEidModal() {
  console.info('TODO: showDownloadEidModal')
}

async function redirectToAppointmentUpdate() {
  console.info('TODO: redirectToAppointmentUpdate')
}

async function viewTicket() {
  console.info('TODO: viewTicket')
}
</script>

<style lang="scss" scoped></style>
